import { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import {
  FiCheckCircle,
  FiFileText,
  FiLink,
  FiMail,
  FiUserCheck,
  FiUsers,
} from 'react-icons/fi';
import { layoutStyles } from 'src/components/dashboard/common/styles';
import {
  breakpointMedium,
  breakpointSmall,
} from 'src/design/styleguide/common/breakpoints';
import { defineGridArea } from 'src/design/styleguide/common/styledComponentsUtils';
import { gridUnit } from 'src/design/styleguide/common/measurements';
import PrimaryButton from 'src/design/components/button/PrimaryButton';
import Loading from 'src/components/loading/Loading';
import AccountSettingsPanel from 'src/components/dashboard/home/AccountSettingsPanel';
import AccountSummaryPanel from 'src/components/dashboard/home/AccountSummaryPanel';
import GreetingPanel from 'src/components/dashboard/home/GreetingPanel';
import AdvisorService from 'scripts/services/AdvisorService';
import { errorAlert } from 'scripts/redux/actions/alerts';
import { userSelector } from 'scripts/redux/actions/user';
import {
  accountSelectorV2,
  fetchAccountInfoV2,
} from 'scripts/redux/actions/account';
import Formatters from 'scripts/utilities/formattersV2';
import Popover from 'scripts/components/shared/Popover';
import Text from 'src/design/styleguide/text/Text';
import {
  BodyTableRow,
  ColumnHeading,
  Table,
  TableData,
} from 'src/design/components/table/Table';
import {
  LinkText,
  SectionDivider,
  SeeMoreLink,
  WelcomePageWidgetPanel,
  WidgetPanelLink,
} from 'src/components/dashboard/home/commonComponents';
import { themeForAdvisorAccount } from 'src/themes/landingPageThemes';
import {
  getBuildYourOwnPlan,
  getPlanFunds,
  getSmartInvestPlan,
} from 'src/utils/account';
import { dollarDecimal } from 'src/utils/formats';
import { ACCOUNTTYPES } from 'src/settings/registration';
import {
  advisorClientOnboardingUrl,
  advisorClientsUrl,
  advisorPendingRequestsUrl,
  advisorSettingsUrl,
  brickxDevelopmentLandingPageUrl,
  brickxMortgageLandingPageUrl,
  privateSyndicationLandingPageUrl,
} from 'src/utils/pageUrls';

const Layout = styled.div`
  ${layoutStyles}
  grid-template-columns: minmax(0, 1fr);
  ${defineGridArea('greeting')}
  ${defineGridArea('accountDivider')}
  ${defineGridArea('summary')}
  ${defineGridArea('dashboardDivider')}
  ${defineGridArea('connect')}
  ${defineGridArea('onboarding')}
  ${defineGridArea('clients')}
  ${defineGridArea('pending')}
  ${defineGridArea('settings')}
  ${defineGridArea('mortgage')}
  ${defineGridArea('developments')}
  ${defineGridArea('syndicate')}

  grid-template-areas:
    'greeting'
    'accountDivider'
    'summary'
    'dashboardDivider'
    'connect'
    'onboarding'
    'clients'
    'pending'
    'settings'
    'mortgage'
    'developments'
    'syndicate';

  ${breakpointMedium(`
    grid-template-columns: repeat(2, minmax(0, 1fr));
    grid-template-areas:
      'greeting greeting'
      'accountDivider accountDivider'
      'summary summary'
      'dashboardDivider dashboardDivider'
      'connect onboarding'
      'clients pending'
      'settings mortgage'
      'developments syndicate';
  `)}
`;

const DashboardCard = styled(WelcomePageWidgetPanel)`
  min-width: 0;
`;

const ActionCardPanel = styled(DashboardCard)`
  padding-top: ${gridUnit * 6}px;
`;

const ProductCardPanel = styled(DashboardCard)`
  padding-top: ${gridUnit * 6}px;
`;

const CardCopy = styled(Text).attrs({
  as: 'p',
  fontSize: '4x-small',
})`
  margin: 0;
  color: var(--bx-color---grey-700);
  line-height: 1.45;
`;

const AdvisorButton = styled(PrimaryButton).attrs({
  color: PrimaryButton.colors.PURPLE,
  size: PrimaryButton.sizes.EXTRA_SMALL,
})`
  && {
    background-color: ${themeForAdvisorAccount.colors.primary};
    white-space: nowrap;
  }
`;

const ActionContent = styled.div`
  display: flex;
  min-height: 236px;
  flex-direction: column;
  align-items: flex-start;
`;

const ActionOverview = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(96px, 40%);
  width: 100%;
  align-items: start;
  gap: ${gridUnit * 2}px;
`;

const ActionCopy = styled.div`
  min-width: 0;
`;

const ActionTitle = styled(Text).attrs({
  as: 'h3',
  fontSize: '2x-small',
  fontWeight: 'medium',
})`
  margin: 0 0 ${gridUnit * 2}px;
`;

const ActionIllustration = styled.img`
  justify-self: end;
  width: 100%;
  max-width: 176px;
  height: 108px;
  object-fit: contain;
  object-position: right top;
`;

const ProcessSteps = styled.div`
  display: grid;
  grid-template-columns:
    minmax(0, 1fr) minmax(28px, 1fr) minmax(0, 1fr) minmax(28px, 1fr)
    minmax(0, 1fr);
  width: 100%;
  align-items: start;
  margin-top: auto;
  padding-top: ${gridUnit * 5}px;
`;

const ProcessStep = styled.div`
  display: flex;
  min-width: 0;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const ProcessIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  border: 2px solid var(--bx-color---purple-600);
  color: var(--bx-color---purple-600);

  > svg {
    width: 28px;
    height: 28px;
  }
`;

const ProcessLabel = styled.span`
  margin-top: ${gridUnit * 3}px;
  color: var(--bx-color---grey-800);
  font-size: 16px;
  line-height: 1.2;
`;

const ProcessArrow = styled.span`
  position: relative;
  align-self: start;
  height: 1px;
  margin: 28px 4px 0;
  background: var(--bx-color---purple-600);

  &::after {
    content: '';
    position: absolute;
    top: -3px;
    right: 0;
    width: 7px;
    height: 7px;
    border-top: 1px solid var(--bx-color---purple-600);
    border-right: 1px solid var(--bx-color---purple-600);
    transform: rotate(45deg);
  }
`;

const ActionButton = styled(AdvisorButton)`
  margin-top: ${gridUnit * 8}px;
`;

const ActionCard = ({
  className,
  title,
  description,
  illustration,
  illustrationAlt,
  processSteps,
  buttonText,
  href,
}) => (
  <ActionCardPanel className={className}>
    <ActionContent>
      <ActionOverview>
        <ActionCopy>
          <ActionTitle>{title}</ActionTitle>
          <CardCopy>{description}</CardCopy>
        </ActionCopy>
        <ActionIllustration src={illustration} alt={illustrationAlt} />
      </ActionOverview>
      <ProcessSteps>
        {processSteps.map(({ Icon: StepIcon, label }, index) => (
          <Fragment key={label}>
            {index > 0 && <ProcessArrow aria-hidden="true" />}
            <ProcessStep>
              <ProcessIcon>
                <StepIcon aria-hidden="true" />
              </ProcessIcon>
              <ProcessLabel>{label}</ProcessLabel>
            </ProcessStep>
          </Fragment>
        ))}
      </ProcessSteps>
      <ActionButton link={href}>{buttonText}</ActionButton>
    </ActionContent>
  </ActionCardPanel>
);

ActionCard.propTypes = {
  className: PropTypes.string,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  illustration: PropTypes.string.isRequired,
  illustrationAlt: PropTypes.string.isRequired,
  processSteps: PropTypes.arrayOf(
    PropTypes.shape({
      Icon: PropTypes.func.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ).isRequired,
  buttonText: PropTypes.string.isRequired,
  href: PropTypes.string.isRequired,
};

const DashboardTable = styled(Table)`
  table {
    border-collapse: collapse;
    border-spacing: 0;
    table-layout: fixed;
  }

  ${ColumnHeading},
  ${TableData} {
    box-sizing: border-box;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  ${TableData} {
    border-bottom: 1px solid var(--bx-color---grey-200);
  }

  ${BodyTableRow} {
    height: 51px;
  }

  ${BodyTableRow}:last-child ${TableData} {
    border-bottom: 0;
  }

  ${ColumnHeading}:nth-child(1),
  ${TableData}:nth-child(1) {
    width: 30%;
  }

  ${ColumnHeading}:nth-child(2),
  ${TableData}:nth-child(2) {
    width: 45%;
  }

  ${ColumnHeading}:nth-child(3),
  ${TableData}:nth-child(3) {
    width: 25%;
    overflow: visible;
    text-overflow: clip;
    white-space: nowrap;
  }
`;

const ClientIdentity = styled.div`
  display: flex;
  min-width: 0;
  align-items: center;
  gap: ${gridUnit * 2}px;
`;

const InitialsAvatar = styled.span`
  display: inline-flex;
  flex: 0 0 24px;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: var(--bx-color---purple-50);
  color: ${themeForAdvisorAccount.colors.primary};
  font-size: 10px;
  font-weight: bold;
`;

const ClientName = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const AccountTypeBadge = styled(Text).attrs({
  as: 'span',
  fontSize: '5x-small',
  fontWeight: 'medium',
})`
  display: inline-flex;
  align-items: center;
  min-height: 20px;
  padding: 2px ${gridUnit * 2}px;
  border-radius: 3px;
  line-height: 1;
  white-space: nowrap;

  &.individual {
    background: var(--bx-color---pink-50);
    color: var(--bx-color---pink-400);
  }

  &.minor {
    background: var(--bx-color---yellow-50);
    color: var(--bx-color---yellow-800);
  }

  &.smsf,
  &.trust,
  &.corporation {
    background: var(--bx-color---blue-50);
    color: var(--bx-color---blue-800);
  }
`;

const EmptyTableMessage = styled(CardCopy)`
  padding: ${gridUnit * 4}px 0;
`;

const ProductContent = styled.div`
  display: grid;
  grid-template-columns: 88px minmax(0, 1fr);
  gap: ${gridUnit * 8}px;
  align-items: center;
  min-height: 124px;

  ${breakpointSmall(`
    grid-template-columns: 112px minmax(0, 1fr);
  `)}
`;

const ProductImage = styled.img`
  width: 100%;
  object-fit: contain;
`;

const ProductTitle = styled(Text).attrs({
  as: 'h3',
  fontSize: '2x-small',
  fontWeight: 'medium',
})`
  margin: 0 0 ${gridUnit * 2}px;
`;

const ProductLinkContainer = styled.div`
  margin-top: ${gridUnit * 3}px;

  ${LinkText} {
    color: ${themeForAdvisorAccount.colors.primary};
  }
`;

const ProductCard = ({
  className,
  title,
  description,
  image,
  imageAlt,
  href,
}) => (
  <ProductCardPanel className={className}>
    <ProductContent>
      <ProductImage src={image} alt={imageAlt} />
      <div>
        <ProductTitle>{title}</ProductTitle>
        <CardCopy>{description}</CardCopy>
        <ProductLinkContainer>
          <WidgetPanelLink text="Learn more" href={href} />
        </ProductLinkContainer>
      </div>
    </ProductContent>
  </ProductCardPanel>
);

ProductCard.propTypes = {
  className: PropTypes.string,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  image: PropTypes.string.isRequired,
  imageAlt: PropTypes.string.isRequired,
  href: PropTypes.string.isRequired,
};

const extractRows = (payload, keys) => {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== 'object') return [];

  for (let index = 0; index < keys.length; index += 1) {
    if (Array.isArray(payload[keys[index]])) return payload[keys[index]];
  }

  return [];
};

const relationshipDate = (relationship) => relationship.updatedAt;

const requestDate = (relationship) => relationship.updatedAt;

const byNewest = (dateGetter) => (left, right) =>
  new Date(dateGetter(right) || 0).getTime() -
  new Date(dateGetter(left) || 0).getTime();

export const latestClients = (clients, limit = 5) =>
  clients.slice().sort(byNewest(relationshipDate)).slice(0, limit);

export const latestPendingRequests = (requests, limit = 5) =>
  requests.slice().sort(byNewest(requestDate)).slice(0, limit);

const clientName = (client) =>
  client.name ||
  client.fullName ||
  client.displayName ||
  [client.givenName, client.middleName, client.familyName]
    .filter(Boolean)
    .join(' ') ||
  '-';

const clientFirstName = (client) => {
  const name = clientName(client);

  return name === '-' ? name : name.split(/\s+/)[0];
};

const getInitials = (name) =>
  name === '-'
    ? ''
    : name
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map((part) => part.charAt(0).toUpperCase())
        .join('');

const accountTypes = {
  [ACCOUNTTYPES.INDIVIDUAL]: 'Individual',
  [ACCOUNTTYPES.MINOR]: 'Minor',
  [ACCOUNTTYPES.SMSF]: 'SMSF',
  [ACCOUNTTYPES.TRUST]: 'Trust',
  [ACCOUNTTYPES.CORPORATION]: 'Company',
};

const accountType = (client) => {
  const value = String(client.accountType || '').toLowerCase();

  return {
    className: value,
    label: accountTypes[value] || client.accountType || '-',
  };
};

const formatDate = (value) => (value ? Formatters.time.date(value) : '-');

const walletBalance = (account) =>
  getPlanFunds(getBuildYourOwnPlan(account)) +
  getPlanFunds(getSmartInvestPlan(account));

const ClientsTable = ({ clients }) => {
  if (!clients.length) {
    return <EmptyTableMessage>No active clients yet.</EmptyTableMessage>;
  }

  return (
    <DashboardTable centeredRows>
      <thead>
        <tr>
          <ColumnHeading>Client</ColumnHeading>
          <ColumnHeading>Email</ColumnHeading>
          <ColumnHeading>Account Type</ColumnHeading>
        </tr>
      </thead>
      <tbody>
        {clients.map((client) => {
          const fullName = clientName(client);
          const name = clientFirstName(client);
          const type = accountType(client);

          return (
            <BodyTableRow key={client.clientId || client.id}>
              <TableData>
                <ClientIdentity>
                  <InitialsAvatar>{getInitials(fullName)}</InitialsAvatar>
                  <ClientName>{name}</ClientName>
                </ClientIdentity>
              </TableData>
              <TableData>{client.email || '-'}</TableData>
              <TableData>
                <AccountTypeBadge className={type.className}>
                  {type.label}
                </AccountTypeBadge>
              </TableData>
            </BodyTableRow>
          );
        })}
      </tbody>
    </DashboardTable>
  );
};

ClientsTable.propTypes = {
  clients: PropTypes.arrayOf(PropTypes.object).isRequired,
};

const PendingRequestsTable = ({ requests }) => {
  if (!requests.length) {
    return <EmptyTableMessage>No pending requests.</EmptyTableMessage>;
  }

  return (
    <DashboardTable centeredRows>
      <thead>
        <tr>
          <ColumnHeading>Client</ColumnHeading>
          <ColumnHeading>Email</ColumnHeading>
          <ColumnHeading>Requested</ColumnHeading>
        </tr>
      </thead>
      <tbody>
        {requests.map((request) => {
          const fullName = clientName(request);
          const name = clientFirstName(request);

          return (
            <BodyTableRow key={request.clientId || request.id || request.email}>
              <TableData>
                <ClientIdentity>
                  <InitialsAvatar>{getInitials(fullName)}</InitialsAvatar>
                  <ClientName>{name}</ClientName>
                </ClientIdentity>
              </TableData>
              <TableData>{request.email || '-'}</TableData>
              <TableData>{formatDate(requestDate(request))}</TableData>
            </BodyTableRow>
          );
        })}
      </tbody>
    </DashboardTable>
  );
};

PendingRequestsTable.propTypes = {
  requests: PropTypes.arrayOf(PropTypes.object).isRequired,
};

@connect(
  (state) => ({
    ...userSelector(state),
    ...accountSelectorV2(state),
  }),
  {
    errorAlert,
    fetchAccountInfo: fetchAccountInfoV2,
  },
)
export default class AdvisorDashboardHome extends Component {
  static propTypes = {
    account: PropTypes.object,
    errorAlert: PropTypes.func.isRequired,
    fetchAccountInfo: PropTypes.func.isRequired,
    user: PropTypes.object.isRequired,
  };

  state = {
    activeClients: [],
    loading: true,
    pendingRequests: [],
  };

  unmounted = false;

  componentDidMount() {
    this.unmounted = false;
    this.loadData();
    this.props.fetchAccountInfo().catch(() => {});
  }

  componentWillUnmount() {
    this.unmounted = true;
  }

  loadData = async () => {
    try {
      const [clientsResponse, requestsResponse] = await Promise.all([
        AdvisorService.getClients(),
        AdvisorService.getPendingRequests(),
      ]);

      if (this.unmounted) return;

      this.setState({
        activeClients: extractRows(clientsResponse, [
          'clients',
          'rows',
          'list',
        ]),
        loading: false,
        pendingRequests: extractRows(requestsResponse, [
          'pendingRequests',
          'requests',
          'pending',
          'rows',
          'list',
        ]),
      });
    } catch (error) {
      if (this.unmounted) return;

      this.props.errorAlert(
        error.message || 'Unable to load adviser dashboard.',
      );
      this.setState({ loading: false });
    }
  };

  render() {
    const { activeClients, loading, pendingRequests } = this.state;
    const { account, user } = this.props;

    if (loading) return <Loading />;

    return (
      <Layout data-test-reference="advisor-dashboard-home">
        <GreetingPanel className="greeting" />

        <SectionDivider className="accountDivider">
          Account Summary
        </SectionDivider>

        <AccountSummaryPanel
          className="summary"
          testRef="advisor-account-summary"
          summaryItems={[
            {
              title: (
                <Fragment>
                  Total Fee Income{' '}
                  <Popover
                    color="onDarkSurfaceContrast"
                    placement="top"
                    content="Total funds in your adviser wallet balance."
                  />
                </Fragment>
              ),
              value: account ? dollarDecimal(walletBalance(account)) : '--',
              valueTestRef: 'total-fee-income',
            },
            {
              title: (
                <Fragment>
                  Active Clients{' '}
                  <Popover
                    placement="top"
                    content="Clients currently connected to your adviser account."
                  />
                </Fragment>
              ),
              value: Formatters.numeric.number(activeClients.length),
              valueTestRef: 'total-clients',
            },
            {
              title: (
                <Fragment>
                  Pending Requests{' '}
                  <Popover
                    placement="top"
                    content="Connection requests awaiting client approval."
                  />
                </Fragment>
              ),
              value: Formatters.numeric.number(pendingRequests.length),
              valueTestRef: 'total-pending-requests',
            },
          ]}
          user={user}
        />

        <SectionDivider className="dashboardDivider">
          Dashboard Summary
        </SectionDivider>

        <ActionCard
          className="connect"
          title="Connect to Client"
          description="Request a connection with an existing BrickX client."
          illustration="/static/img/brickx-advisor/illustration-connect-client.png"
          illustrationAlt="Connection request approval process"
          processSteps={[
            { Icon: FiUsers, label: 'Existing client' },
            { Icon: FiMail, label: 'Approval email' },
            { Icon: FiLink, label: 'Connected' },
          ]}
          buttonText="Connect Client"
          href={advisorPendingRequestsUrl()}
        />

        <ActionCard
          className="onboarding"
          title="Client Onboarding"
          description="Onboard your client to BrickX and link them to your adviser profile."
          illustration="/static/img/brickx-advisor/illustration-onboard-client.png"
          illustrationAlt="Client onboarding process"
          processSteps={[
            { Icon: FiFileText, label: 'Create account' },
            { Icon: FiUserCheck, label: 'Client activates' },
            { Icon: FiCheckCircle, label: 'Onboarded' },
          ]}
          buttonText="Onboard Client"
          href={advisorClientOnboardingUrl()}
        />

        <DashboardCard
          className="clients"
          title="Recent Clients"
          action={<SeeMoreLink href={advisorClientsUrl()} />}
        >
          <ClientsTable clients={latestClients(activeClients)} />
        </DashboardCard>

        <DashboardCard
          className="pending"
          title="Latest Requests"
          action={<SeeMoreLink href={advisorPendingRequestsUrl()} />}
        >
          <PendingRequestsTable
            requests={latestPendingRequests(pendingRequests)}
          />
        </DashboardCard>

        <AccountSettingsPanel
          className="settings"
          settingsHref={advisorSettingsUrl()}
          testRef="advisor-account-settings-panel"
        />

        <ProductCard
          className="mortgage"
          title="BrickX Mortgages"
          description="Help your clients diversify beyond property equity with first mortgage investment opportunities."
          image="/static/img/brickx-advisor/illustration-brickx-mortgage.png"
          imageAlt="BrickX Mortgages illustration"
          href={brickxMortgageLandingPageUrl()}
        />

        <ProductCard
          className="developments"
          title="BrickX Developments"
          description="Give suitable clients access to property development opportunities managed through BrickX."
          image="/static/img/brickx-advisor/illustration-brickx-development.png"
          imageAlt="BrickX Developments illustration"
          href={brickxDevelopmentLandingPageUrl()}
        />

        <ProductCard
          className="syndicate"
          title="BrickX Select & Syndicate"
          description="Help clients take more control by choosing a property and investing through their own syndicate."
          image="/static/img/brickx-advisor/illustration-brickx-select-and-syndicate.png"
          imageAlt="BrickX Select and Syndicate illustration"
          href={privateSyndicationLandingPageUrl()}
        />
      </Layout>
    );
  }
}
